/**
 * Type efficacy decoder for the dossier MOVES + MATCHUPS tabs.
 *
 * Eng review EN-1 (2026-05-13) corrected the design's prior phantom API
 * `Dex.types.get(t).effectiveness` — the real `@pkmn/dex` exposes type
 * efficacy as a packed integer code on `TypeInfo.damageTaken`:
 *
 *   0 → neutral (1×)
 *   1 → weak    (2×)   attacker IS super-effective vs this defender
 *   2 → resist  (½×)   defender resists this attacker
 *   3 → immune  (0×)
 *
 * `damageTaken[attackerTypeName]` is read FROM the defender's record. The
 * canonical Gen-3 vector: Onix (Rock/Ground) defending against Water is
 *
 *   dex.types.get('Rock')?.damageTaken['Water']   = 1 (weak → 2×)
 *   dex.types.get('Ground')?.damageTaken['Water'] = 1 (weak → 2×)
 *   product                                       = 4× ← canonical test
 *
 * All three exported functions are SYNCHRONOUS. The caller is responsible
 * for warming `@pkmn/dex` (eager-warmed at boot via main.ts) and passing
 * the resolved `Dex` value through. This keeps the primitive pure +
 * vitest-testable: tests pass a mock matching the structural shape; the
 * dossier MOVES + MATCHUPS tabs pass the real `Dex` (or `loadGen3().types`
 * if Gen-3-correct semantics are needed — both expose the same
 * `damageTaken` integer-code contract).
 *
 * Companion to `apps/game/src/battle/typeChart.ts` which serves the
 * battle action-menu effectiveness badge via `Type.totalEffectiveness`.
 * They overlap conceptually but split by surface: battle wants a sync
 * multiplier per move-vs-defender; dossier wants chip strips + bucketed
 * defensive charts. The new primitive's domain-specific outputs make
 * sharing one helper awkward (the eng review pinned a fresh primitive).
 */

/** Integer code → multiplier. @pkmn/dex's TypeInfo.damageTaken encoding. */
const INT_TO_MULT: Record<number, number> = { 0: 1, 1: 2, 2: 0.5, 3: 0 };

/**
 * Minimal structural type of the `dex` argument. Accepts the real
 * `import('@pkmn/dex').Dex` singleton, the `.types` subset of an
 * `@pkmn/data` `Generation`, or any test mock that matches the shape.
 */
export interface EfficacyDex {
  types: {
    get(name: string): { damageTaken: Record<string, number> } | null | undefined;
  };
}

/**
 * All 17 Gen-3 attacker / defender type names in canonical chart order.
 * Used by `computeOffensiveChips` + `computeDefensiveChips` to iterate
 * the matrix. Fairy was added in Gen 6 and is intentionally excluded —
 * v0.1 ships Gen-3-substrate species only.
 */
export const GEN3_TYPES: readonly string[] = [
  'Normal',   'Fire',     'Water',    'Electric',
  'Grass',    'Ice',      'Fighting', 'Poison',
  'Ground',   'Flying',   'Psychic',  'Bug',
  'Rock',     'Ghost',    'Dragon',   'Dark',
  'Steel',
];

/** Title-case a type name for @pkmn/dex's case-sensitive `types.get`. */
function capitalizeType(t: string): string {
  if (!t) return t;
  return t[0]!.toUpperCase() + t.slice(1).toLowerCase();
}

/**
 * Multiplier of `attacker` damaging a single-type `defender`. Accepts
 * either case — 'Water' or 'water' resolve identically. Returns 1
 * (neutral) for unknown types or missing chart entries — defensive
 * default so a content gap (newer-gen type the v0.1 chart doesn't
 * carry) never renders a stale or incorrect badge.
 */
export function getEfficacy(
  attacker: string,
  defender: string,
  dex: EfficacyDex,
): number {
  const type = dex.types.get(capitalizeType(defender));
  if (!type) return 1;
  const code = type.damageTaken[capitalizeType(attacker)];
  if (code === undefined) return 1;
  return INT_TO_MULT[code] ?? 1;
}

/** One row in an offensive- or defensive-chip strip. */
export interface EfficacyChip {
  /** Title-case type name ('Water', 'Electric'). */
  type: string;
  /** Decoded multiplier — one of {0, 0.25, 0.5, 1, 2, 4}. */
  multiplier: number;
}

/**
 * Per-move offensive chips for the dossier MOVES tab right-edge strip.
 * Samples all 17 defender types against `moveType` and returns chips
 * for super-effective (2×) and immunity (0×) hits ONLY.
 *
 * The compressed set per DR-7 / Premise 2: ½× resists are OMITTED on
 * the MOVES row strip (clutters the right-edge budget at iPhone SE
 * 375 px). The full set lives in `computeDefensiveChips` for the
 * MATCHUPS chart instead.
 *
 * Returned chips are sorted multiplier-DESC, then type-ASC inside
 * each bucket — super-effective chips appear before immunity chips
 * with stable alphabetical order within each.
 */
export function computeOffensiveChips(
  moveType: string,
  dex: EfficacyDex,
): EfficacyChip[] {
  const out: EfficacyChip[] = [];
  for (const defender of GEN3_TYPES) {
    const m = getEfficacy(moveType, defender, dex);
    if (m === 2 || m === 0) out.push({ type: defender, multiplier: m });
  }
  out.sort((a, b) => {
    if (b.multiplier !== a.multiplier) return b.multiplier - a.multiplier;
    return a.type.localeCompare(b.type);
  });
  return out;
}

/** Bucket key for the defensive-matchup chart. Stringified multipliers. */
export type DefensiveBucketKey = '4' | '2' | '0.5' | '0.25' | '0';

/** Defender's matchup chart — attacker types grouped by total multiplier. */
export type DefensiveBucketMap = Record<DefensiveBucketKey, string[]>;

/** Multiplier → bucket key. Neutral (1×) is omitted — no bucket. */
const BUCKET_FOR_MULT: Map<number, DefensiveBucketKey> = new Map([
  [4, '4'],
  [2, '2'],
  [0.5, '0.5'],
  [0.25, '0.25'],
  [0, '0'],
]);

/**
 * Defensive matchup chart for a single- or dual-type Pokémon. For each
 * attacker type X, computes `getEfficacy(X, type1) * getEfficacy(X, type2)`
 * (the second factor defaults to 1× when only one defender type is
 * supplied). Buckets attackers by total multiplier into 4× / 2× / ½× /
 * ¼× / 0× lists; neutral 1× is OMITTED (no bucket) — neutral is the
 * baseline and renders as absence.
 *
 * Buckets without any attackers are returned as empty arrays. The
 * MATCHUPS chart (DR-8) drops empty rows from rendering — Bulbasaur has
 * no 4× weakness, so its rendered chart skips the 4× row entirely.
 *
 * Canonical test vectors (EN-1):
 *   - Onix (Rock, Ground)         → 4× includes Water and Grass.
 *   - Bulbasaur (Grass, Poison)   → 4× bucket is EMPTY (counter-example).
 *
 * Types within each bucket are sorted alphabetical for stable rendering.
 */
export function computeDefensiveChips(
  types: readonly [string] | readonly [string, string],
  dex: EfficacyDex,
): DefensiveBucketMap {
  const out: DefensiveBucketMap = {
    '4': [],
    '2': [],
    '0.5': [],
    '0.25': [],
    '0': [],
  };
  const t1 = types[0];
  const t2 = types.length > 1 ? types[1] : undefined;
  for (const attacker of GEN3_TYPES) {
    const m1 = getEfficacy(attacker, t1, dex);
    const m2 = t2 ? getEfficacy(attacker, t2, dex) : 1;
    const total = m1 * m2;
    const bucket = BUCKET_FOR_MULT.get(total);
    if (bucket === undefined) continue; // 1× = neutral; not bucketed.
    out[bucket].push(attacker);
  }
  for (const key of Object.keys(out) as DefensiveBucketKey[]) {
    out[key].sort((a, b) => a.localeCompare(b));
  }
  return out;
}
